// The one SpacetimeDB connection (11 §11.1 SessionStore, A2). `DbConnection.builder()` from the
// generated bindings; the anonymous identity + token the server issues on first connect is kept
// in localStorage (per database name) so reloads keep the same identity. `withCompression('none')`
// dodges the Safari gzip bug. The builder is handed to `SpacetimeDBProvider`, which owns connect /
// reconnect; `onConnect` publishes the live connection here for non-React code (stores, scene).
// docs/54: once the session has connected, a dropped socket is the LinkStore's (the SDK reconnects
// the same `DbConnection` and replays its subscriptions); only the boot path ends in `offline`.
import { Identity, Timestamp } from 'spacetimedb';
import { DbConnection, tables, type DbConnectionBuilder } from '../../bindings';
import { useSession } from '../stores/session';
import { useLink } from '../stores/link';
import { deviceNow } from '../time';

export const STDB_URI: string = import.meta.env.VITE_STDB_URI ?? 'ws://127.0.0.1:3000';
export const STDB_DB: string = import.meta.env.VITE_STDB_DB ?? 'fellowkeep-local';
const TOKEN_KEY = `fk.stdb.token.${STDB_DB}`;
export const CLIENT_VERSION: string = typeof __FK_CLIENT_VERSION__ === 'string' ? __FK_CLIENT_VERSION__ : '0.0.0';

let conn: DbConnection | undefined;
let builder: DbConnectionBuilder | undefined;
let builtFor = -1;
/** The session reached the World at least once (from here a drop is a link drop, not a boot failure). */
let everConnected = false;
const connectListeners = new Set<(c: DbConnection) => void>();
const reconnectListeners = new Set<(c: DbConnection) => void>();

export function getConn(): DbConnection | undefined { return conn && conn.isActive ? conn : undefined; }
export function requireConn(): DbConnection {
  if (!conn) throw new Error('not connected');
  return conn;
}
/** docs/54: runs `cb` after the SDK has reconnected the same connection (before its replay applies). */
export function onReconnected(cb: (c: DbConnection) => void): () => void {
  reconnectListeners.add(cb);
  return () => reconnectListeners.delete(cb);
}
/** Runs `cb` now if connected, and on every new connection (not the SDK's in-place reconnects). */
export function onConnected(cb: (c: DbConnection) => void): () => void {
  connectListeners.add(cb);
  if (conn?.isActive) cb(conn);
  return () => connectListeners.delete(cb);
}

export function loadToken(): string | undefined {
  try {
    if (new URLSearchParams(location.search).has('fresh')) { localStorage.removeItem(TOKEN_KEY); return undefined; }
    return localStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch { return undefined; }
}
export function saveToken(token: string): void { try { localStorage.setItem(TOKEN_KEY, token); } catch { /* private mode */ } }
export function clearToken(): void { try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } }

/** The connection builder the Provider connects with: built once, and again after a `useLink.rebuild()`
 * (docs/54 RC3), which re-reads the token so a stale one dropped on a connect error is not reused. */
export function connectionBuilder(opts: { rulesetVersion: number }): DbConnectionBuilder {
  const rebuilds = useLink.getState().rebuilds;
  if (builder && builtFor === rebuilds) return builder;
  builtFor = rebuilds;
  watchDevice();
  const token = loadToken();
  let b = DbConnection.builder().withUri(STDB_URI).withDatabaseName(STDB_DB).withCompression('none');
  if (token) b = b.withToken(token);
  builder = b
    .onConnect((c, identity, tok) => {
      conn = c;
      everConnected = true;
      saveToken(tok);
      useLink.getState().restored();
      useSession.getState().connected(identity.toHexString(), tok);
      // 11 §11.4 compat gate: the module's ruleset must match the wasm's.
      c.subscriptionBuilder().onApplied((ctx) => {
        const row = [...ctx.db.clientCompat.iter()][0];
        useSession.getState().setCompat(row ? { rulesetVersion: row.rulesetVersion, minClient: row.minClient, moduleVersion: row.moduleVersion } : undefined, opts.rulesetVersion, CLIENT_VERSION);
      }).onError((_ctx: unknown, err?: Error) => { console.error('[stdb] client_compat subscription failed', err); useSession.getState().setCompat(undefined, opts.rulesetVersion, CLIENT_VERSION); })
        .subscribe(tables.clientCompat);
      for (const l of connectListeners) l(c);
    })
    .onAutomaticReconnect((c) => {
      console.info('[stdb] reconnected');
      conn = c;
      useLink.getState().restored();
      for (const l of reconnectListeners) l(c);
    })
    .onConnectError((_ctx, err, nextAttempt, delayMs) => {
      console.error('[stdb] connect error', err, nextAttempt);
      const message = err?.message ?? String(err);
      if (everConnected) { useLink.getState().dropped(message, { attempt: nextAttempt, delayMs }); return; }
      // A token the server no longer knows is the common sticky failure after a reset: drop it so the retry gets a fresh identity.
      if (/token|auth|401|403/i.test(message)) clearToken();
      useLink.getState().dropped(message, { attempt: nextAttempt, delayMs });
      useSession.getState().offline(message);
    })
    .onDisconnect((_ctx, err, nextAttempt, delayMs) => {
      console.warn('[stdb] disconnected', err, nextAttempt);
      useLink.getState().dropped(err ? err.message : 'disconnected', { attempt: nextAttempt, delayMs });
      if (!everConnected) useSession.getState().offline(err ? err.message : 'disconnected');
    });
  return builder;
}

/** docs/54 RC3 Reconnect now: an SDK still retrying tries at once (its own `online` resume hook);
 * a connection the SDK has given up on, or a boot that never connected, gets a new builder. */
export function reconnectNow(): void {
  const link = useLink.getState();
  if (link.ended || !everConnected || !conn) { link.rebuild(); return; }
  window.dispatchEvent(new Event('online'));
}

let watching = false;
/** `navigator.onLine` flips before a socket notices: show the strip at once, and clear it if the socket lived. */
function watchDevice(): void {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  window.addEventListener('offline', () => {
    useLink.getState().setDeviceOffline(true);
    if (everConnected) useLink.getState().dropped('this device is offline', { attempt: 1 });
  });
  // a tick later the SDK's own `online` listener has dropped a socket that died while we were
  // away; one still open means nothing was lost
  window.addEventListener('online', () => {
    useLink.getState().setDeviceOffline(navigator.onLine === false);
    setTimeout(() => { if (conn?.isActive && useLink.getState().state !== 'online') useLink.getState().restored(); }, 0);
  });
}

// ---- small conversions shared by the subscriptions and stores ------------------------------

export const identityHex = (id: Identity | undefined | null): string => (id ? id.toHexString() : '');
export const sameIdentity = (a: Identity | string | undefined, b: Identity | string | undefined): boolean =>
  (typeof a === 'string' ? a : identityHex(a)) === (typeof b === 'string' ? b : identityHex(b));
export const tsToMs = (ts: Timestamp | undefined | null): number | undefined => (ts ? Number(ts.microsSinceUnixEpoch / 1000n) : undefined);
export const shortId = (hex: string): string => `wanderer-${hex.slice(-6)}`;
export const parseIdentity = (hex: string): Identity => Identity.fromString(hex);

/** Half a round trip of the last reducer call, for clock sync. */
export function measureRtt<T>(p: Promise<T>): Promise<{ value: T; rttMs: number }> {
  const t0 = deviceNow();
  return p.then((value) => ({ value, rttMs: deviceNow() - t0 }));
}
